import AuthService from './AuthService';

/**
 * Client for the OGC API surface exposed by the DTS API gateway (decision D1:
 * the UI talks to DTS directly, not through OJM):
 *   GET  /collections/{resource}/items      — OGC API - Records (catalog)
 *   POST /processes/validate/execution      — chain_config validation (process)
 *   POST /processes/estimate/execution      — cost/duration/size estimate (process)
 *   GET  /ops/products/availability          — EDL product pre-check (operational)
 */
class DataTailorService {
  /** Resolve the gateway base. Absolute dtsBaseUrl wins (prod); otherwise the
   * dev '/dts' proxy declared in setupProxy.js. */
  static apiUrl(path) {
    const configured = (window.APP_CONFIG.dtsBaseUrl || '').replace(/\/$/, '');
    if (configured) return `${configured}${path}`;
    return `${process.env.PUBLIC_URL || ''}/dts${path}`;
  }

  static useDummy() {
    return !!window.APP_CONFIG.featureFlags?.useDummyData;
  }

  static authHeaders() {
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${AuthService.getToken()}`,
    };
  }

  /** End the session and redirect on an expired/invalid token, mirroring
   * OrderService. Throws so callers stop processing the dead response. */
  static check401(res) {
    if (res.status === 401) {
      AuthService.handleSessionExpired();
      throw new Error('Unauthorized');
    }
  }

  /** DT catalog resources, each an OGC API - Records collection. */
  static catalogResources() {
    return ['products', 'chains', 'formats', 'projections', 'rois', 'aggregations', 'compressions'];
  }

  static async getCatalog() {
    if (this.useDummy()) {
      const res = await fetch(`${process.env.PUBLIC_URL || ''}/dummy-data/dt-catalog.json`);
      return res.json();
    }
    // OGC API - Records: one record collection per DT resource. Aggregate the
    // collections back into the {resources:{...}} shape the UI consumes; each
    // record's properties carry the original Data Tailor fields verbatim.
    const resources = {};
    await Promise.all(this.catalogResources().map(async (r) => {
      const res = await fetch(this.apiUrl(`/collections/${r}/items?limit=10000`), {
        headers: this.authHeaders(),
      });
      this.check401(res);
      if (!res.ok) { resources[r] = []; return; }
      const fc = await res.json();
      resources[r] = (fc.features || []).map((f) => f.properties || {});
    }));
    return { resources };
  }

  static async validateChain(chainConfig) {
    if (this.useDummy()) {
      const errors = [];
      if (!chainConfig.product) errors.push("'product' is required");
      if (!chainConfig.format) errors.push("'format' is required");
      return { valid: errors.length === 0, errors: errors.length ? errors : null };
    }
    // Validation is the 'validate' OGC process; the result document is the verdict.
    const res = await fetch(this.apiUrl('/processes/validate/execution'), {
      method: 'POST',
      headers: this.authHeaders(),
      body: JSON.stringify({ inputs: { chainConfig } }),
    });
    this.check401(res);
    if (!res.ok) throw new Error(`Validation failed (status ${res.status})`);
    return res.json();
  }

  static async estimateCost({ chainConfig, nrInputProducts, startTime, stopTime }) {
    if (this.useDummy()) {
      const n = nrInputProducts || 1;
      return {
        valid: true,
        estimatedDurationSec: 30 * Math.max(1, n),
        estimatedMemoryMb: 400,
        estimatedSizeGb: Number((0.2 * n).toFixed(3)),
        estimatedCost: Number((0.5 * n).toFixed(2)),
      };
    }
    // Quotation is the 'estimate' OGC process; the result document is the quote.
    const res = await fetch(this.apiUrl('/processes/estimate/execution'), {
      method: 'POST',
      headers: this.authHeaders(),
      body: JSON.stringify({ inputs: { chainConfig, nrInputProducts, startTime, stopTime } }),
    });
    this.check401(res);
    if (!res.ok) throw new Error(`Estimate failed (status ${res.status})`);
    return res.json();
  }

  /**
   * Product-availability pre-check against the EDL catalogue (proxied by the
   * gateway, which holds the api-key). Returns {available, reason} where
   * available is true/false when verified and null when the check could not be
   * performed - callers must NOT block the order on null (catalogue outage).
   */
  static async checkProductAvailability(collectionId, productId) {
    if (this.useDummy()) return { available: null, reason: 'dummy data mode' };
    try {
      const params = new URLSearchParams({ collection: collectionId, product: productId });
      const res = await fetch(this.apiUrl(`/ops/products/availability?${params}`), {
        headers: this.authHeaders(),
      });
      this.check401(res);
      if (!res.ok) return { available: null, reason: `check unavailable (status ${res.status})` };
      return await res.json();
    } catch (e) {
      return { available: null, reason: 'check unavailable' };
    }
  }

  /** pn_id is string | string[] | "NA" per the DT API reference; normalize to a list. */
  static pnIdsOf(product) {
    const v = product?.pn_id;
    if (!v || v === 'NA') return [];
    return (Array.isArray(v) ? v : [v]).filter((x) => x && x !== 'NA');
  }

  /** Catalogue-collection compatibility of a DT product class:
   * true = registered for the collection, false = registered for OTHER
   * collections only (the job would fail epct's input check), null = unknown
   * (no pn_id in the catalog - never block on unknown). */
  static isProductCompatible(product, collectionId) {
    if (!collectionId) return null;
    const ids = this.pnIdsOf(product);
    if (ids.length === 0) return null;
    return ids.includes(collectionId);
  }

  static productsForCollection(products, collectionId) {
    return (products || []).filter((p) => this.isProductCompatible(p, collectionId) === true);
  }

  /**
   * Serialises a chain_config object to YAML for submission. JSON is valid
   * YAML, but this emits readable indented YAML for the known shallow DT shape
   * (scalars, string arrays, and one level of nested objects).
   */
  static chainConfigToYaml(cfg) {
    const lines = [];
    const emit = (key, value, indent) => {
      const pad = '  '.repeat(indent);
      if (value === null || value === undefined || value === '') return;
      if (Array.isArray(value)) {
        if (value.length === 0) return;
        lines.push(`${pad}${key}:`);
        value.forEach((item) => lines.push(`${pad}  - ${item}`));
      } else if (typeof value === 'object') {
        const entries = Object.entries(value).filter(([, v]) => v !== null && v !== undefined && v !== '');
        if (entries.length === 0) return;
        lines.push(`${pad}${key}:`);
        entries.forEach(([k, v]) => emit(k, v, indent + 1));
      } else {
        lines.push(`${pad}${key}: ${value}`);
      }
    };
    Object.entries(cfg || {}).forEach(([k, v]) => emit(k, v, 0));
    return lines.join('\n');
  }
}

export default DataTailorService;
